// @flow
/* eslint-disable react/require-default-props */
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import {
  useLocation,
  useNavigate,
  matchRoutes,
  useRoutes
} from 'react-router-dom';
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import ConnectedSnackBar from '@justpark/snackbar/src/components/ConnectedSnackBar/ConnectedSnackBar';
import { toSnakeCase } from '@justpark/helpers/src/caseConverter/caseConverter';
import VhResizer from '@justpark/ui/src/components/VhResizer';
import ConnectedHeader from '../components/Global/Header';
import Footer from '../components/Global/Footer/Footer';
import config from '../../config/config';
import withoutAppBanner from '../helpers/config';
import getRoutes from '../routes';
import withAnalytics from '../components/Decorators/withAnalytics';
import { setUser } from '../helpers/BugsnagClient';
import BrowserContext from '../context/BrowserContext';
import UserContext from '../context/UserContext';
import BrandContext from '../context/BrandContext';
import GlobalErrorBoundary from './GlobalErrorBoundary';
import { selectIsAppFeatureLimited } from '../stores/partner';
import useClearDataOnLogout from '../hooks/useClearDataOnLogout';
import LazyPromoModal from '../components/Common/LazyPromoModal';
import { logout } from '../stores/auth';
import { gtag, gtagTrack } from '../helpers/Tracking/Analytics';
import { selectIsWebview } from '../stores/ui';
import useResetWhiteLabel from '../hooks/useResetWhiteLabel';
import OAuthTokenReceiver from '../components/Auth/OAuthTokenReceiver';

const getHelmetConfiguration = (pathname) => {
  const conf =
    pathname.startsWith('/search') || pathname.includes('/partner/')
      ? withoutAppBanner(config)
      : config;
  return conf.app.head;
};

type Props = {
  isAppFeatureLimited: boolean,
  user?: any,
  brand?: {
    isWhiteLabel: boolean
  },
  browser?: any,
  route: any
};

const AppHooks = () => {
  const navigate = useNavigate();

  useEffect(() => {
    if (__CLIENT__ && window.Cypress) {
      window.routerPush = navigate;
    }
  }, [navigate]);

  return null;
};

// Maps through routes, ensures loadable component is rendered as a component
// and recursively through children
// UseRoutes does not work with loadable components without this
// https://github.com/remix-run/react-router/issues/7421
const mapRoutesForUse = (routes) =>
  routes.map((item) => {
    const { path, children, element } = item;
    const Element = element;

    return {
      path,
      element: element ? <Element /> : null,
      children: children && mapRoutesForUse(children)
    };
  });

export const Routes = () => {
  const routes = getRoutes();

  return useRoutes(mapRoutesForUse(routes));
};

const useResetScroll = () => {
  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    window.scroll(0, 0);
  }, [pathname]);
};

const App = (props: Props) => {
  const { isAppFeatureLimited, brand, browser, user } = props;
  const location = useLocation();
  const { pathname } = location;
  const isWebview = useSelector(selectIsWebview);

  // Current route
  const branch = matchRoutes(getRoutes(), pathname);

  // Tests to pass to matchRoutes that gives us an object with booleans on about what type of pages we are on
  // based on the metadata of the route, or its url, or parameters it has in the uri / querystring.
  // We use these to inform the global header and footer of their behaviour for these pages.
  const globalModifiers = {
    searchResults: ({ route }) =>
      route.meta && route.meta.routeType === 'search',
    monthlyCheckout: ({ route }) =>
      route.meta && route.meta.routeType === 'checkout',
    hidePrimaryNav: ({ route }) =>
      route.meta && route.meta.headerHidePrimaryNav,
    minimalBackground: ({ route }) =>
      route.meta && route.meta.headerMinimalBackground,
    darkMobileMenu: ({ route }) =>
      route.meta && route.meta.headerDarkMobileMenu,
    hideFooter: ({ route }) => route.meta && route.meta.hideFooter,
    hideFindParking: ({ route }) => route.meta && route.meta.hideFindParking,
    hideCPM: ({ route }) => route.path.startsWith('/dashboard'),
    hideHeader: ({ route }) => route.meta && route.meta.hideHeader
  };
  Object.keys(globalModifiers).forEach((key) => {
    const test = globalModifiers[key];
    globalModifiers[key] = branch.some(test);
  });

  useResetScroll();
  useClearDataOnLogout();
  useResetWhiteLabel();

  return (
    <>
      <AppHooks />
      <GlobalErrorBoundary>
        <BrandContext.Provider value={brand}>
          <UserContext.Provider value={user}>
            <BrowserContext.Provider value={browser}>
              <div>
                <div id="portal" />

                {/* <FullStory /> TODO: FEND-1174 moves this behind a feature flag */}
                <Helmet {...getHelmetConfiguration(pathname)} />

                {!isWebview && !globalModifiers.hideHeader && (
                  <ConnectedHeader user={user} theme={globalModifiers} />
                )}
                <GlobalErrorBoundary>
                  <div id="app-content">
                    <Routes />
                  </div>

                  {!isWebview &&
                    !globalModifiers.hideFooter &&
                    !isAppFeatureLimited && <Footer />}
                  <ConnectedSnackBar />
                  <LazyPromoModal />
                </GlobalErrorBoundary>
              </div>
              <VhResizer />
              <OAuthTokenReceiver />
            </BrowserContext.Provider>
          </UserContext.Provider>
        </BrandContext.Provider>
      </GlobalErrorBoundary>
    </>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
  browser: state.browser,
  brand: state.brand,
  isAppFeatureLimited: selectIsAppFeatureLimited(state)
});

const valueObservers = {
  bookingCount: [
    (state) => state?.auth?.user?.bookingSummary?.totalBookingsMade || 0,
    ({ state, analytics }) => {
      if (state && state.auth) {
        const defaultBookingSummary = {
          latestBookingDate: null,
          firstBookingDate: null,
          totalAppBookingsMade: 0,
          totalBookingsMade: 0
        };

        const { user } = state.auth;

        const defaultProperties = user
          ? user.bookingSummary
          : defaultBookingSummary;

        analytics.setDefaultProperties(toSnakeCase(defaultProperties));
      }
    }
  ],
  userId: [
    (state) => state?.auth?.user?.id || null,
    ({ state, analytics }) => {
      if (state && state.auth) {
        const { user } = state.auth;

        if (user && __CLIENT__ && 'exponea' in window) {
          window.exponea.identify(user.id);
        }

        setUser(user);

        const userId = state?.auth?.user?.id || null;

        gtag({
          userId
        });

        const isLoggedIn = Boolean(user);

        analytics.setDefaultProperties({
          is_logged_in: isLoggedIn
        });
      }
    }
  ],
  features: [
    (state) => state?.features?.features || [],
    ({ state, analytics }) => {
      if (state && state.features.features.length > 0) {
        const properties = {};
        state.features.features.forEach((feature) => {
          properties[`flag_enabled_${feature.key}`] = feature.enabled;
          properties[`flag_variant_${feature.key}`] = feature.variantKey;

          if (
            feature.variantAttachment &&
            feature.variantAttachment?.experiment_id &&
            feature.variantAttachment?.variant_id
          ) {
            gtagTrack('optimize_experiment', {
              feature: feature.key,
              experimentId: feature.variantAttachment.experiment_id,
              variantId: feature.variantAttachment.variant_id
            });
          }
        });

        analytics.setDefaultProperties(properties);
      }
    }
  ],
  launchDarkly: [
    (state) => state?.launchDarkly?.ids || [],
    ({ state, analytics }) => {
      if (state && state.launchDarkly.ids.length > 0) {
        const properties = {};

        state.launchDarkly.ids.forEach((id) => {
          const feature = state.launchDarkly.entities[id];
          properties[`launch-darkly-${id}`] = feature.value;
        });

        analytics.setDefaultProperties(properties);
      }
    }
  ]
};

export const analyticsEventSubscribers = {
  [logout.fulfilled]: () => {
    if (__CLIENT__ && 'exponea' in window) {
      window.exponea.anonymize();
    }
  }
};

export default compose(
  connect(mapStateToProps),
  withAnalytics(valueObservers, analyticsEventSubscribers)
)(App);
